import { useInfiniteQuery } from '@tanstack/react-query';
import {
  FORMAS_DE_FECHAMENTO,
  nomeNaComanda,
  totalDaComanda,
  type ComandaAberta,
  type FormaDeFechamento,
  type TotaisPorForma,
} from '@arena/shared/schemas';
import { dinheiro } from '../agenda/formatos.js';
import { mensagemDoErro } from '../api/cliente.js';
import { estoqueApi } from '../api/estoque.js';
import { Erro, EsqueletoDeTabela } from '../ui/estados.jsx';
import { Botao, Campo, Card, Escolhas, Nota, Quem, Tabela, Vazio } from '../ui/primitivos.jsx';
import {
  FORMA_NO_HISTORICO,
  consultaDoHistorico,
  contagemDeItens,
  formasComValor,
  lerFiltrosDoHistorico,
  quandoEncerrou,
  somaDosTotais,
} from './formatos.js';

/**
 * O histórico de `#v-bar`: as comandas que já fecharam (ou foram
 * canceladas), com a soma por forma de pagamento sob os filtros.
 *
 * Os filtros moram na URL — `?aba=historico&de=&ate=&forma=&canceladas=1` —,
 * como em `#v-planos`: o link colado abre o mesmo recorte. Mudar o filtro usa
 * `replace`, então o voltar do navegador sai da página, não desfaz o filtro.
 * Quem lê a URL é `lerFiltrosDoHistorico`, que ignora o que não presta em
 * vez de mandar ao servidor um pedido que ele vai recusar.
 *
 * A soma vem do SERVIDOR e cobre o filtro inteiro: somar aqui as linhas
 * carregadas daria um total diferente a cada "Carregar mais".
 *
 * Ver: Arena_backend/Docs/plano/13-estoque-comandas.md
 */

type FormaOuTodas = FormaDeFechamento | 'todas';

const OPCOES_DE_FORMA: readonly { valor: FormaOuTodas; rotulo: string }[] = [
  { valor: 'todas', rotulo: 'Todas' },
  ...FORMAS_DE_FECHAMENTO.map((forma) => ({ valor: forma, rotulo: FORMA_NO_HISTORICO[forma] })),
];

export function HistoricoDeComandas({
  params,
  mudar,
  onAbrir,
}: {
  params: URLSearchParams;
  mudar: (mudanca: (p: URLSearchParams) => void) => void;
  onAbrir: (comandaId: string) => void;
}) {
  const filtros = lerFiltrosDoHistorico(params);
  const { canceladas, de, ate, forma, periodoInvertido } = filtros;

  const historico = useInfiniteQuery({
    queryKey: ['comandas', 'historico', canceladas, de ?? '', ate ?? '', forma ?? ''],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      estoqueApi.historicoDeComandas(consultaDoHistorico(filtros, pageParam)),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (ultima) => ultima.proximoCursor ?? undefined,
    enabled: !periodoInvertido,
  });

  const itens = historico.data?.pages.flatMap((pagina) => pagina.itens) ?? [];
  // O total da página mais recente: é a que o servidor somou por último.
  const totais = historico.data?.pages.at(-1)?.totaisPorForma;
  const filtrado = Boolean(de || ate || forma || canceladas);

  function definir(campo: 'de' | 'ate', valor: string) {
    mudar((p) => (valor ? p.set(campo, valor) : p.delete(campo)));
  }

  return (
    <Card titulo={canceladas ? 'Comandas canceladas' : 'Comandas fechadas'}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          gap: '12px 16px',
          marginBottom: 14,
        }}
      >
        <Campo rotulo="De">
          <input
            type="date"
            value={de ?? ''}
            max={ate}
            onChange={(e) => definir('de', e.target.value)}
          />
        </Campo>
        <Campo rotulo="Até">
          <input
            type="date"
            value={ate ?? ''}
            min={de}
            onChange={(e) => definir('ate', e.target.value)}
          />
        </Campo>
        {!canceladas && (
          <Campo rotulo="Forma de pagamento">
            <Escolhas
              aria-label="Filtrar pela forma de pagamento"
              opcoes={OPCOES_DE_FORMA}
              valor={forma ?? 'todas'}
              onChange={(valor) =>
                mudar((p) => (valor === 'todas' ? p.delete('forma') : p.set('forma', valor)))
              }
            />
          </Campo>
        )}
        <label className="consent" style={{ margin: 0 }}>
          <input
            className="ck"
            type="checkbox"
            checked={canceladas}
            onChange={(e) =>
              mudar((p) => {
                if (e.target.checked) {
                  p.set('canceladas', '1');
                  // Cancelada não foi paga: a forma não se aplica.
                  p.delete('forma');
                } else {
                  p.delete('canceladas');
                }
              })
            }
          />
          <span>Ver canceladas</span>
        </label>
        {filtrado && (
          <Botao
            variante="fantasma"
            pequeno
            onClick={() =>
              mudar((p) => {
                for (const campo of ['de', 'ate', 'forma', 'canceladas']) p.delete(campo);
              })
            }
          >
            Limpar filtros
          </Botao>
        )}
      </div>

      {periodoInvertido && <Nota>A data inicial vem depois da final. Ajuste o período.</Nota>}

      {!periodoInvertido && !canceladas && totais && <LinhaDeTotais totais={totais} />}

      {!periodoInvertido && historico.isPending && <EsqueletoDeTabela colunas={5} />}
      {historico.isError && (
        <Erro
          detalhe={mensagemDoErro(historico.error)}
          onTentarDeNovo={() => void historico.refetch()}
        />
      )}
      {historico.data && itens.length === 0 && (
        <Vazio>
          {canceladas
            ? 'Nenhuma comanda cancelada neste período.'
            : 'Nenhuma comanda fechada neste filtro.'}
        </Vazio>
      )}
      {itens.length > 0 && (
        <>
          <TabelaDoHistorico comandas={itens} canceladas={canceladas} onAbrir={onAbrir} />
          {historico.hasNextPage && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '14px 0 2px' }}>
              <Botao
                variante="contorno"
                pequeno
                onClick={() => void historico.fetchNextPage()}
                disabled={historico.isFetchingNextPage}
              >
                {historico.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
              </Botao>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

/**
 * "Total no filtro R$ 1.500 · Pix R$ 1.000 · Dinheiro R$ 500" — só as
 * formas em que entrou valor. Fica entre os filtros e a tabela porque é
 * a resposta à pergunta que os filtros fazem.
 */
function LinhaDeTotais({ totais }: { totais: TotaisPorForma }) {
  const soma = somaDosTotais(totais);
  return (
    <div
      aria-live="polite"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'baseline',
        gap: '4px 18px',
        padding: '10px 14px',
        marginBottom: 14,
        background: 'var(--bg)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-sm)',
      }}
    >
      <span className="sub">
        Total no filtro{' '}
        <b className="num" style={{ color: 'var(--txt)', fontSize: 16 }}>
          {dinheiro(soma)}
        </b>
      </span>
      {formasComValor(totais).map(({ forma, valor }) => (
        <span key={forma} className="sub">
          {FORMA_NO_HISTORICO[forma]} <b className="num">{dinheiro(valor)}</b>
        </span>
      ))}
    </div>
  );
}

function TabelaDoHistorico({
  comandas,
  canceladas,
  onAbrir,
}: {
  comandas: readonly ComandaAberta[];
  canceladas: boolean;
  onAbrir: (comandaId: string) => void;
}) {
  return (
    <Tabela
      colunas={
        canceladas
          ? ['Cancelada em', 'Pessoa', 'Motivo']
          : ['Fechada em', 'Pessoa', 'Itens', 'Forma', 'Total']
      }
    >
      {comandas.map((comanda) => (
        <tr
          key={comanda.id}
          className="clickable"
          style={{ cursor: 'pointer' }}
          tabIndex={0}
          onClick={() => onAbrir(comanda.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault(); // o Espaço não rola a página
              onAbrir(comanda.id);
            }
          }}
        >
          <td>{quandoEncerrou(comanda)}</td>
          <td>
            <Quem nome={nomeNaComanda(comanda)} detalhe={comanda.observacao ?? undefined} />
          </td>
          {canceladas ? (
            <td>{comanda.motivoCancelamento ?? '—'}</td>
          ) : (
            <>
              <td>{contagemDeItens(comanda)}</td>
              <td>{comanda.formaPagamento ? FORMA_NO_HISTORICO[comanda.formaPagamento] : '—'}</td>
              <td className="num">{dinheiro(comanda.total ?? totalDaComanda(comanda.itens))}</td>
            </>
          )}
        </tr>
      ))}
    </Tabela>
  );
}
